import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  apiErrorText,
  useCreatePrcVariantMutation,
  useDeletePrcVariantMutation,
  useUpdatePrcVariantMutation,
  type PrcVariantInfo,
  type PrcVersionPayload,
  type PrcVersionSummary,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import { typo } from '@entities/fleet';
import { PRC_MAIN_LABEL, prcStatusLabel, takenObjects } from '@entities/prc';
import { Control } from '@widgets/FleetRegistry/formFields';
import { Actions, FooterStatus, Notice } from '@widgets/FleetRegistry/ui';
import { describedBy, DrawerTitle, Eyebrow, FormField, HeaderRow, Titles } from '../drawerParts';
import { ObjectsField } from '../ObjectsField';
import { Lead, SchemeScope } from '../ui';

/**
 * Вариант условий версии: создать (как основной расчёт или с условиями другой
 * версии — например, отдельно загруженной книги района) или изменить
 * название и объекты, удалить. Цены, ФОТ и параметры варианта правятся во
 * вкладках версии.
 */
export function VariantDrawer({
  payload,
  versions,
  variant,
  onClose,
  onSaved,
}: {
  payload: PrcVersionPayload;
  versions: readonly PrcVersionSummary[];
  /** Изменяемый вариант; `null` — новый. */
  variant: PrcVariantInfo | null;
  onClose: () => void;
  /** Какой вариант показать после сохранения; `null` — основной расчёт. */
  onSaved: (variantId: string | null) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const v = payload.version;
  const variants = payload.variants ?? [];
  const [name, setName] = useState(variant?.name ?? '');
  const [objectIds, setObjectIds] = useState<string[]>(
    () => variant?.objects.map((o) => o.id) ?? [],
  );
  const [fromId, setFromId] = useState('');
  const [touched, setTouched] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [create, createState] = useCreatePrcVariantMutation();
  const [update, updateState] = useUpdatePrcVariantMutation();
  const [remove, removeState] = useDeletePrcVariantMutation();
  const busy = createState.isLoading || updateState.isLoading || removeState.isLoading;

  const taken = useMemo(
    () => takenObjects(v.objects, variants, variant?.id ?? ''),
    [v.objects, variants, variant],
  );
  // Условия из другой версии — того же горизонта: у варианта он общий.
  const sources = versions.filter(
    (x) => x.id !== v.id && (x.base_year === null || x.base_year === v.params.base_year),
  );
  const nameError = name.trim() === '' ? 'Назовите вариант — например, по району' : null;
  const showNameError = touched && nameError !== null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (nameError) return;
    try {
      if (variant) {
        const res = await update({
          id: v.id,
          variantId: variant.id,
          name: name.trim(),
          project_ids: objectIds,
          expected_updated_at: v.updated_at,
        }).unwrap();
        onSaved(res.variant?.id ?? variant.id);
      } else {
        const res = await create({
          id: v.id,
          name: name.trim(),
          project_ids: objectIds,
          from_version_id: fromId || undefined,
          expected_updated_at: v.updated_at,
        }).unwrap();
        onSaved(res.variant?.id ?? null);
      }
    } catch (err) {
      setError(apiErrorText(err, 'Вариант не сохранился.'));
    }
  }

  async function doDelete() {
    if (!variant) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setError(null);
    try {
      await remove({
        id: v.id,
        variantId: variant.id,
        expected_updated_at: v.updated_at,
      }).unwrap();
      onSaved(null);
    } catch (err) {
      setError(apiErrorText(err, 'Вариант не удалился.'));
    }
  }

  const ids = { name: `${uid}-name`, from: `${uid}-from`, objects: `${uid}-objects` };

  return (
    <Drawer open onClose={onClose} labelledBy={`${uid}-title`} initialFocusRef={titleRef}>
      <SchemeScope>
        <DrawerHeader>
          <HeaderRow>
            <Titles>
              <Eyebrow>{typo(`Версия «${v.label}» · условия расчёта`)}</Eyebrow>
              <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
                {variant ? `Вариант «${variant.name}»` : 'Новый вариант условий'}
              </DrawerTitle>
            </Titles>
            <DrawerCloseButton onClick={onClose} />
          </HeaderRow>
        </DrawerHeader>
        <DrawerBody>
          <Lead>
            {typo(
              'Вариант — те же модели, паспорта и лимиты, что в основном расчёте, но свои условия района: цены топлива, ФОТ механизаторов, общецеховые и всё, что поменяете во вкладках. Чего вариант не менял, берётся из основного расчёта. При утверждении версии цены варианта записываются на его объекты.',
            )}
          </Lead>
          {error && (
            <Notice $tone="danger" role="alert">
              <span className="text">{error}</span>
            </Notice>
          )}
          <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
            <FormField
              id={ids.name}
              label="Название"
              required
              error={showNameError ? nameError : null}
            >
              <Control
                id={ids.name}
                value={name}
                autoComplete="off"
                placeholder="Например: Тюменская область (Тобольск)"
                aria-required="true"
                aria-invalid={showNameError ? true : undefined}
                aria-describedby={describedBy(ids.name, false, showNameError)}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(true)}
              />
            </FormField>
            {!variant && (
              <FormField
                id={ids.from}
                label="Условия"
                hint={typo(
                  fromId
                    ? 'Своими станут разделы, которыми та версия отличается от основного расчёта (например, топливо, общецеховые, ФОТ). Модели и лимиты — этой версии.'
                    : 'Начнёт как основной расчёт — цены, ФОТ и параметры поменяете во вкладках.',
                )}
              >
                <Select
                  id={ids.from}
                  value={fromId}
                  aria-describedby={`${ids.from}-hint`}
                  onChange={(e) => setFromId(e.target.value)}
                >
                  <option value="">{`Как в основном расчёте`}</option>
                  {sources.length > 0 && (
                    <optgroup label="Взять условия из версии">
                      {sources.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.label} — {prcStatusLabel(x.status).toLowerCase()}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </Select>
              </FormField>
            )}
            <ObjectsField
              id={ids.objects}
              value={objectIds}
              onChange={setObjectIds}
              disabled={busy}
              taken={taken}
              hint={
                objectIds.length === 0
                  ? 'Отметьте объекты района: на них при утверждении запишутся цены варианта. Без объектов вариант не отправить на утверждение.'
                  : `Цены варианта запишутся на отмеченные объекты; на объектах основного расчёта — «${PRC_MAIN_LABEL.toLowerCase()}».`
              }
            />
          </form>
          {variant && (
            <Notice $tone={confirmDelete ? 'danger' : 'info'} role="note">
              <span className="text">
                {typo(
                  confirmDelete
                    ? `Удалить вариант «${variant.name}» с его условиями? Модели и основной расчёт останутся.`
                    : 'Вариант можно удалить, пока версия — черновик.',
                )}
              </span>
              <Button
                type="button"
                $variant={confirmDelete ? 'danger' : 'ghost'}
                $size="sm"
                disabled={busy}
                onClick={() => void doDelete()}
              >
                {confirmDelete ? 'Да, удалить вариант' : 'Удалить вариант'}
              </Button>
            </Notice>
          )}
        </DrawerBody>
        <DrawerFooter>
          <FooterStatus role="status">{busy ? 'Сохраняю и пересчитываю…' : ''}</FooterStatus>
          <Actions>
            <Button type="button" $variant="ghost" onClick={onClose} disabled={busy}>
              Отмена
            </Button>
            <Button type="submit" form={`${uid}-form`} $variant="primary" disabled={busy}>
              {variant ? 'Сохранить' : 'Создать вариант'}
            </Button>
          </Actions>
        </DrawerFooter>
      </SchemeScope>
    </Drawer>
  );
}
